import React from 'react';

import {
  AddIcon,
  CmBox,
  CmButton,
  CmCheckBox,
  CmFlex,
  CmFormErrorMessage,
  CmFormLabel,
  CmHeading,
  CmHelpterText,
  CmLabelAndInput,
  CmRadio,
  CmRadioGroup,
  CmStack,
} from '@/components/uiParts';
import { orderMethods } from '@/domain/models/Order/orderMethod';
import { OrderMethod } from '@/pages/camel-order/sites/components/CustomFieldForm/type';
import { CustomField, siteForm } from '@/pages/camel-order/sites/service';
import { SiteFormError } from '@/pages/camel-order/sites/validate';

type Props = {
  customFields: CustomField[];
  handleRemoveCustomField: (customFieldIndex: number) => void;
  handleChangeCustomField: (
    customFieldIndex: number,
    field: keyof CustomField,
    value: string
  ) => void;
  isOrderMethodChecked: (
    orderMethod: OrderMethod,
    customField: CustomField
  ) => boolean;
  handleChangeCustomFieldOrderMethod: (
    isChecked: boolean,
    customFieldIndex: number,
    optionValue: OrderMethod
  ) => void;
  handleAddCustomField: () => void;
  error: SiteFormError;
};

export const CustomFieldForm: React.FC<Props> = (props) => {
  const {
    customFields,
    handleRemoveCustomField,
    handleChangeCustomField,
    isOrderMethodChecked,
    handleChangeCustomFieldOrderMethod,
    handleAddCustomField,
    error,
  } = props;
  return (
    <CmBox mt="20px">
      <CmHeading fontSize="18px" pb="10px">
        カスタム注文フィールド設定（注文確認画面での入力項目）
      </CmHeading>
      <CmBox pl="20px">
        {customFields.length > 0 && (
          <CmStack spacing="15px" mb="15px">
            {customFields.map((customField, customFieldIndex) => (
              <>
                <CmFlex
                  key={customFieldIndex}
                  maxWidth="300px"
                  alignItems="end"
                >
                  <CmFormLabel fontWeight="bold">{`追加項目${
                    customFieldIndex + 1
                  }`}</CmFormLabel>
                  <CmButton
                    ml="15px"
                    colorScheme="red"
                    onClick={() => {
                      handleRemoveCustomField(customFieldIndex);
                    }}
                  >
                    削除
                  </CmButton>
                </CmFlex>

                <CmBox mt="5px">
                  <CmLabelAndInput
                    isRequired
                    label={siteForm.customFieldName.label}
                    id={siteForm.customFieldName.key}
                    value={customField['name']}
                    onChange={(e) =>
                      handleChangeCustomField(
                        customFieldIndex,
                        'name',
                        e.target.value
                      )
                    }
                    isInvalid={error[siteForm.customFieldName.key].isError}
                  >
                    <CmFormErrorMessage>
                      {error[siteForm.customFieldName.key].message}
                    </CmFormErrorMessage>
                  </CmLabelAndInput>
                  <CmHelpterText mt="3px">80字まで</CmHelpterText>
                </CmBox>

                <CmStack spacing="15px">
                  <CmFormLabel isRequired>対象の注文方式</CmFormLabel>
                  {orderMethods.map((option, index) => (
                    <CmCheckBox
                      key={index}
                      ml={index > 0 ? '20px' : undefined}
                      isChecked={isOrderMethodChecked(
                        option.value,
                        customField
                      )}
                      value={option.value}
                      onChange={(e) => {
                        handleChangeCustomFieldOrderMethod(
                          e.target.checked,
                          customFieldIndex,
                          option.value
                        );
                      }}
                    >
                      {option.name}
                    </CmCheckBox>
                  ))}
                </CmStack>

                <CmBox>
                  <CmFormLabel isRequired>任意・必須</CmFormLabel>
                  <CmRadioGroup
                    value={customField['required'] ? '1' : '0'}
                    onChange={(value: string) => {
                      handleChangeCustomField(
                        customFieldIndex,
                        'required',
                        value
                      );
                    }}
                  >
                    <CmStack direction="row">
                      <CmRadio size="md" value={'0'}>
                        任意
                      </CmRadio>
                      <CmRadio size="md" value={'1'}>
                        必須
                      </CmRadio>
                    </CmStack>
                  </CmRadioGroup>
                </CmBox>

                <CmBox>
                  <CmFormLabel isRequired>フィールドのタイプ</CmFormLabel>
                  <CmRadioGroup
                    value={customField['custom_field_type']}
                    onChange={(value: string) => {
                      handleChangeCustomField(
                        customFieldIndex,
                        'custom_field_type',
                        value
                      );
                    }}
                  >
                    <CmStack direction="row">
                      <CmRadio size="md" value={'TEXT'}>
                        短い回答
                      </CmRadio>
                      <CmRadio size="md" value={'TEXTAREA'}>
                        段落
                      </CmRadio>
                    </CmStack>
                  </CmRadioGroup>
                </CmBox>

                <CmBox mt="5px">
                  <CmLabelAndInput
                    isRequired
                    label={siteForm.customFieldDescription.label}
                    id={siteForm.customFieldDescription.key}
                    value={customField['description']}
                    onChange={(e) =>
                      handleChangeCustomField(
                        customFieldIndex,
                        'description',
                        e.target.value
                      )
                    }
                    isInvalid={
                      error[siteForm.customFieldDescription.key].isError
                    }
                  >
                    <CmFormErrorMessage>
                      {error[siteForm.customFieldDescription.key].message}
                    </CmFormErrorMessage>
                  </CmLabelAndInput>
                  <CmHelpterText mt="3px">200字まで</CmHelpterText>
                </CmBox>

                <CmLabelAndInput
                  label={'最低文字数'}
                  id={siteForm.customFieldMinimum.key}
                  value={customField['minimum']}
                  onChange={(e) =>
                    handleChangeCustomField(
                      customFieldIndex,
                      'minimum',
                      e.target.value
                    )
                  }
                  isInvalid={error[siteForm.customFieldMinimum.key].isError}
                >
                  <CmFormErrorMessage>
                    {error[siteForm.customFieldMinimum.key].message}
                  </CmFormErrorMessage>
                </CmLabelAndInput>

                <CmBox mt="5px">
                  <CmLabelAndInput
                    label={'最大文字数'}
                    id={siteForm.customFieldMaximum.key}
                    value={customField['maximum']}
                    onChange={(e) =>
                      handleChangeCustomField(
                        customFieldIndex,
                        'maximum',
                        e.target.value
                      )
                    }
                    isInvalid={error[siteForm.customFieldMaximum.key].isError}
                  >
                    <CmFormErrorMessage>
                      {error[siteForm.customFieldMaximum.key].message}
                    </CmFormErrorMessage>
                  </CmLabelAndInput>

                  <CmHelpterText mt="3px">600字まで</CmHelpterText>
                </CmBox>

                <CmLabelAndInput
                  label={'正規表現'}
                  placeholder="正規表現を入力してください。例：半角英数字指定の場合→^[a-zA-Z0-9]+$"
                  id={siteForm.customFieldRegex.key}
                  value={customField['regex']}
                  onChange={(e) =>
                    handleChangeCustomField(
                      customFieldIndex,
                      'regex',
                      e.target.value
                    )
                  }
                  isInvalid={error[siteForm.customFieldRegex.key].isError}
                >
                  <CmFormErrorMessage>
                    {error[siteForm.customFieldRegex.key].message}
                  </CmFormErrorMessage>
                </CmLabelAndInput>
              </>
            ))}
          </CmStack>
        )}

        {customFields.length < 3 && (
          // @ts-ignore TS2590
          <CmButton
            leftIcon={<AddIcon />}
            variant="outline"
            onClick={handleAddCustomField}
          >
            新しい注文フィールドを追加
          </CmButton>
        )}
      </CmBox>
    </CmBox>
  );
};
